import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Phone,
  Sparkles,
  Building2,
  Check,
  Search,
  FileText,
  Users,
} from "lucide-react";
import DossierCard from "@/components/DossierCard";
import DossierHoverPreview from "@/components/DossierHoverPreview";
import ProductSuggestionForm from "@/components/ProductSuggestionForm";
import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useReveal } from "@/hooks/useReveal";
import { cn } from "@/lib/utils";
import { listScrollKey, saveScrollForList } from "@/lib/scrollRestore";
import { Seo } from "@/components/Seo";
import { DEFAULT_DESCRIPTION, SITE_NAME, getCanonicalOrigin } from "@/config/site";

/** Classes pour apparition au scroll (texte inchangé). */
function landingRevealCls(visible) {
  return cn(
    "transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none",
    visible
      ? "opacity-100 translate-y-0"
      : "opacity-0 translate-y-7 motion-reduce:translate-y-0 motion-reduce:opacity-100"
  );
}

/** Affiché si l’API ne renvoie aucun dossier ; sans compte, les clics mènent à l’inscription (sinon marketplace). */
const LANDING_FALLBACK_DOSSIERS = [
  {
    dossier_id: "landing-preview-1",
    annonce_numero: "RM-2026-000101",
    secteur: "Énergie",
    type: "vente",
    price: 275,
    title: "Photovoltaïque résidentiel : toiture libre, câblage prêt pour raccordement",
    description: "Propriétaires avec consommation annuelle estimée, pas d’ombre portée majeure et créneau téléphonique confirmé pour devis 3 à 9 kWc.",
    location: "Sud & Occitanie",
    created_at: "2026-04-18T10:00:00.000Z",
    seller_name: "Pro",
    seller_kyc_status: "approved",
    seller_rating: 4.8,
    seller_rating_count: 14,
  },
  {
    dossier_id: "landing-preview-2",
    annonce_numero: "RM-2026-000102",
    secteur: "Énergie",
    type: "vente",
    price: 220,
    title: "Pompe à chaleur air/eau : remplacement chaudière fioul ou gaz",
    description: "Ménages avec surface habitable et ancien mode de chauffage renseigné, visite technique souhaitée sous 10 jours.",
    location: "Grand Est & Bourgogne",
    created_at: "2026-04-17T14:30:00.000Z",
    seller_name: "Pro",
    seller_kyc_status: "approved",
    seller_rating: 4.7,
    seller_rating_count: 11,
  },
  {
    dossier_id: "landing-preview-3",
    annonce_numero: "RM-2026-000103",
    secteur: "BTP",
    type: "vente",
    price: 265,
    title: "Rénovation d’ampleur : isolation, menuiseries, chauffage (parcours MaPrimeRénov)",
    description: "DPE initial C ou D, reste à charge estimée et volonté de signer avec entreprise RGE — dossier documentaire déjà engagé.",
    location: "Île-de-France & Normandie",
    created_at: "2026-04-16T09:15:00.000Z",
    seller_name: "Pro",
    seller_kyc_status: "approved",
    seller_rating: 4.9,
    seller_rating_count: 19,
  },
  {
    dossier_id: "landing-preview-4",
    annonce_numero: "RM-2026-000104",
    secteur: "Assurance",
    type: "vente",
    price: 145,
    title: "Multirisque habitation : résiliation à l’échéance ou sinistre récent",
    description: "Contacts avec demande de rappel courtier, opt-in conforme et créneau téléphonique validé pour devis comparatif.",
    location: "National",
    created_at: "2026-04-15T11:45:00.000Z",
    seller_name: "Cabinet",
    seller_kyc_status: "approved",
    seller_rating: 4.7,
    seller_rating_count: 21,
  },
];

const HOW_BUYER_STEPS = [
  {
    Icon: Search,
    title: null,
    body: "Je parcours la marketplace, je choisis mon RDV, mon argent est sécurisé sur la plateforme.",
  },
  {
    Icon: Phone,
    title: null,
    body: "J’appelle le client via notre plateforme anonyme et sécurisée.",
  },
  {
    Icon: Check,
    title: null,
    body: "Si je confirme le RDV je suis débité, si je le refuse pour une raison légitime je suis remboursé.",
  },
];

const HOW_SELLER_STEPS = [
  {
    Icon: FileText,
    title: null,
    body: "Je publie mon dossier : je décris le prospect et je fixe mon prix. Il apparaît tout de suite sur la marketplace.",
  },
  {
    Icon: Users,
    title: null,
    body: "L’acheteur appelle mon client via la plateforme, de façon anonyme et sécurisée. Il n’a jamais accès aux coordonnées du prospect. Les appels sont enregistrés et analysés par une IA anti-contournement.",
  },
  {
    Icon: ShieldCheck,
    title: null,
    body: "L’acheteur confirme le rendez-vous : je suis payé automatiquement. S’il refuse, on s’appuie sur l’enregistrement de l’appel et l’analyse IA pour trancher.",
  },
];

function HowStepCard({ stepNum, Icon, title, body }) {
  return (
    <div className="flex h-full min-w-0 flex-col rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm ring-1 ring-black/[0.03] transition-shadow hover:shadow-md sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#2D7FF9]/10 font-display text-sm font-bold text-[#2D7FF9]">
          {stepNum}
        </span>
        <Icon className="h-6 w-6 shrink-0 text-[#2D7FF9]" strokeWidth={2} aria-hidden />
      </div>
      {title ? (
        <h3 className="mb-3 font-display text-lg font-bold leading-snug tracking-tight text-[#0B0D12]">{title}</h3>
      ) : null}
      <div
        className={cn(
          "flex-1 break-words text-pretty leading-[1.7] text-slate-600",
          title ? "text-[15px]" : "font-display text-[17px] font-semibold leading-snug text-[#0B0D12]"
        )}
      >
        {body}
      </div>
    </div>
  );
}

const Landing = () => {
  const { user } = useAuth();
  const location = useLocation();
  const landingScrollKey = useMemo(
    () => listScrollKey(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const [dossiers, setDossiers] = useState([]);
  const [produits, setProduits] = useState([]);
  /** Section Comment ça marche : acheteur par défaut */
  const [howRole, setHowRole] = useState("buyer"); // "buyer" | "seller"
  const fallbackCtaTo = user ? "/marketplace" : "/register";
  const revealHow = useReveal();
  const revealDossiers = useReveal();

  useEffect(() => {
    api.get("/dossiers").then(({ data }) => setDossiers(data.slice(0, 4))).catch(() => {});
    api.get("/produits").then(({ data }) => setProduits(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const origin = getCanonicalOrigin();
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${origin}/#website`,
        url: origin,
        name: SITE_NAME,
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${origin}/#organization` },
        inLanguage: "fr-FR",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${origin}/marketplace?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "Organization",
        "@id": `${origin}/#organization`,
        name: SITE_NAME,
        url: origin,
        description: DEFAULT_DESCRIPTION,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo description={DEFAULT_DESCRIPTION} jsonLd={structuredData} canonicalPath="/" />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#E5E7EB]">
        <div className="absolute inset-0 -z-10" aria-hidden>
          <div className="absolute inset-0 bg-gradient-to-br from-slate-100 via-[#e8f1ff] to-[#cfe4ff]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-white" />
        </div>
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-16 sm:px-6 sm:pt-24 sm:pb-20 md:pt-32 md:pb-24 lg:pt-40 lg:pb-28">
          <div className="mx-auto flex max-w-4xl flex-col items-center text-center motion-safe:animate-landing-hero motion-reduce:animate-none">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#2D7FF9]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#2D7FF9] sm:mb-8" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5" />
              Marketplace vérifiée
            </div>
            <h1 className="mb-5 font-display text-[1.65rem] font-black leading-[1.08] tracking-tighter text-balance text-[#0B0D12] sm:mb-6 sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
              La marketplace<br />
              <span className="text-[#2D7FF9]">des rendez-vous qualifiés.</span>
            </h1>
            <p className="mx-auto max-w-3xl text-base leading-relaxed text-pretty text-slate-700 sm:text-lg md:text-xl md:leading-[1.65]">
              Achetez et vendez des dossiers clients en toute confiance. Un cadre sécurisé, des appels anonymisés, une équipe
              d’arbitrage. <strong>Les deux parties sont protégées à chaque étape</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* COMMENT ÇA MARCHE — section unique (#comment) */}
      <section
        id="comment"
        ref={revealHow.ref}
        className={cn(
          "scroll-mt-24 border-b border-[#E5E7EB] bg-gradient-to-b from-[#F8FAFC] via-white to-[#FAFBFC] py-20 md:py-28",
          landingRevealCls(revealHow.visible)
        )}
      >
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="font-display font-black text-3xl tracking-tighter text-[#0B0D12] text-balance sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12] after:mt-5 after:mx-auto after:block after:h-0.5 after:w-14 after:rounded-full after:bg-[#2D7FF9]/25 after:content-['']">
              Comment ça marche ?
            </h2>
          </div>

          <div
            className="mx-auto mb-12 flex max-w-xl flex-wrap items-center justify-center gap-x-1 gap-y-2 border-b border-slate-200"
            role="tablist"
            aria-label="Parcours acheteur ou vendeur"
          >
            <button
              type="button"
              role="tab"
              aria-selected={howRole === "buyer"}
              onClick={() => setHowRole("buyer")}
              className={cn(
                "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors sm:px-5",
                howRole === "buyer"
                  ? "border-[#2D7FF9] text-[#2D7FF9]"
                  : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900"
              )}
            >
              J&apos;achète des dossiers
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={howRole === "seller"}
              onClick={() => setHowRole("seller")}
              className={cn(
                "-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors sm:px-5",
                howRole === "seller"
                  ? "border-[#2D7FF9] text-[#2D7FF9]"
                  : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900"
              )}
            >
              Je vends des dossiers
            </button>
          </div>

          {howRole === "buyer" && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {HOW_BUYER_STEPS.map(({ Icon, title, body }, i) => (
                <HowStepCard key={`buyer-${i}`} stepNum={i + 1} Icon={Icon} title={title} body={body} />
              ))}
            </div>
          )}

          {howRole === "seller" && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {HOW_SELLER_STEPS.map(({ Icon, title, body }, i) => (
                <HowStepCard key={`seller-${i}`} stepNum={i + 1} Icon={Icon} title={title} body={body} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* LATEST DOSSIERS */}
      <section
        ref={revealDossiers.ref}
        className={cn("mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24", landingRevealCls(revealDossiers.visible))}
      >
        <div className="mb-10 flex flex-col gap-6 sm:mb-14 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-balance font-display text-2xl font-black tracking-tighter text-[#0B0D12] after:mt-4 after:block after:h-0.5 after:w-14 after:rounded-full after:bg-[#2D7FF9]/25 after:content-[''] sm:text-3xl sm:after:mt-5 md:text-4xl lg:text-5xl">
              Derniers dossiers<br />
              <span className="text-[#2D7FF9]">en ligne.</span>
            </h2>
            <p className="mt-5 max-w-xl text-base text-slate-700 leading-relaxed text-pretty">
              La marketplace s’enrichit au fil des publications des vendeurs vérifiés, quel que soit le secteur. Même
              présentation pour chaque annonce : prix clair, confiance affichée, règles identiques pour tout le monde.
            </p>
          </div>
          <Button asChild variant="outline" className="h-11 w-full shrink-0 sm:h-10 sm:w-auto" data-testid="see-all-dossiers">
            <Link to={fallbackCtaTo}>
              Tout voir <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3 xl:grid-cols-4 [&>*]:overflow-visible">
          {(dossiers.length === 0 ? LANDING_FALLBACK_DOSSIERS : dossiers).map((d) => {
            const isDemo = dossiers.length === 0;
            const card = (
              <DossierCard
                dossier={d}
                to={isDemo ? fallbackCtaTo : undefined}
                onBeforeOpen={isDemo ? undefined : () => saveScrollForList(landingScrollKey)}
              />
            );
            if (isDemo) return card;
            return (
              <DossierHoverPreview
                key={d.dossier_id}
                dossier={d}
                dossierId={d.dossier_id}
                produits={produits}
                href={`/dossier/${d.dossier_id}`}
                onBeforeOpen={() => saveScrollForList(landingScrollKey)}
              >
                {card}
              </DossierHoverPreview>
            );
          })}
        </div>
      </section>

      <section id="contact" className="border-t border-[#E5E7EB] py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid items-start gap-8 md:grid-cols-2 md:gap-10">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#2D7FF9]/10 text-[#2D7FF9] px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase mb-4">
                Contact & suggestions
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-[1.18] mb-4 text-[#0B0D12] text-balance">
                Un besoin ? Écrivez-nous en quelques mots.
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Question simple, partenariat, idée de produit : le formulaire s’adapte. Choisissez le type d’échange et
                envoyez.
              </p>
            </div>

            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
              <ProductSuggestionForm context="landing" />
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E7EB] py-10">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 text-sm text-slate-500 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <Building2 className="w-4 h-4 shrink-0" />
              <span className="text-center sm:text-left">© 2026 RdvMarket — Marketplace des rendez-vous qualifiés</span>
            </div>
            <nav
              className="flex flex-wrap items-center justify-center sm:justify-end gap-x-6 gap-y-2 text-slate-600"
              aria-label="Liens pied de page"
            >
              <Link to={{ pathname: "/", hash: "comment" }} className="hover:text-[#2D7FF9]" data-testid="footer-how">
                Comment ça marche
              </Link>
              <Link to="/marketplace" className="hover:text-[#2D7FF9]" data-testid="footer-marketplace">
                Marketplace
              </Link>
              <Link to={{ pathname: "/", hash: "contact" }} className="hover:text-[#2D7FF9]" data-testid="footer-contact">
                Contact
              </Link>
              <Link to="/login" className="hover:text-[#2D7FF9]" data-testid="footer-login">
                Connexion
              </Link>
              <Link to="/register" className="hover:text-[#2D7FF9]" data-testid="footer-register">
                Créer un compte
              </Link>
              <Link to="/cgu" className="hover:text-[#2D7FF9]" data-testid="footer-cgu-link">
                CGU
              </Link>
              <Link to="/charte" className="hover:text-[#2D7FF9]" data-testid="footer-charter-link">
                Charte RGPD
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
